import React from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSelector } from 'react-redux';
import { ABSOLUTE, PERCENTAGE, ALLOWANCE } from '../../constants/defaults';
import {
  getChargeReasonCodeOptionByCode,
  getDiscountReasonCodeOptionByCode,
  getTaxCategoryOptionByCode,
  getUOMOptionByCode,
} from '../../api/codelist/codeListsSlice';
import { currencyFormatter, formatXmlUnitPrice } from '../../utils/currencyFormat';

const getDesiredValues = (amount, base_amount, multiplier_factor_numeric) => {
  if (!!base_amount && !!multiplier_factor_numeric) {
    return {
      calculatedAmount: (base_amount * multiplier_factor_numeric) / 100,
      percentageText: `(${multiplier_factor_numeric}% of ${base_amount})`,
      typeText: PERCENTAGE,
    };
  } else if (amount) {
    return {
      calculatedAmount: amount,
      percentageText: '',
      typeText: ABSOLUTE,
    };
  }
};

const formatAllowanceChargeAmount = (value, isDocumentLevel) =>
  isDocumentLevel ? currencyFormatter().format(Number(value) || 0) : formatXmlUnitPrice(value);

const AllowanceChargeFieldRow = ({ item, index, type, isDocumentLevel, onEdit, onDelete, readOnly = false }) => {
  const isAllowance = type === ALLOWANCE;
  const reasonCodeOption = useSelector(
    isAllowance
      ? getDiscountReasonCodeOptionByCode(item.reason_code)
      : getChargeReasonCodeOptionByCode(item.reason_code),
  );

  const taxCategoryCodeOption = useSelector(getTaxCategoryOptionByCode(item.tax_category));

  const reasonCodeLabel = () => {
    if (!item.reason_code) return '';
    return reasonCodeOption ? `(${reasonCodeOption.code} - ${reasonCodeOption.label}) ` : '';
  };

  const taxInfoLabel = () => {
    if (!isDocumentLevel) return '';
    const taxCategory = item.tax_category;
    const taxRate = item.tax_rate;
    if (!taxCategory || taxRate === undefined) return '';

    return taxCategoryCodeOption
      ? `(${taxCategoryCodeOption.code} - ${taxCategoryCodeOption.label} (${taxRate}%))`
      : '';
  };

  const prefix = isAllowance ? '-' : '+';
  const amount = item.amount;
  const base_amount = item.base_amount;
  const multiplier_factor_numeric = item.multiplier_factor_numeric;

  const {
    calculatedAmount = 0,
    percentageText = '',
    typeText = '',
  } = getDesiredValues(amount, base_amount, multiplier_factor_numeric) || {};

  return (
    <Stack
      key={item.id}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={2}
      sx={{
        p: 2,
        py: 1,
        border: '1px solid',
        borderColor: grey[800],
        borderRadius: 1,
        backgroundColor: grey[100],
      }}
    >
      <Stack spacing={0.5} sx={{ flex: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              px: 1,
              py: 0.25,
              borderRadius: 1,
              backgroundColor: 'white',
              border: '1px solid',
              borderColor: grey[400],
            }}
          >
            <Typography fontSize={12}>{typeText}</Typography>
          </Box>
          <Typography variant="caption" fontWeight="medium">
            {isDocumentLevel && taxInfoLabel()}
            {reasonCodeLabel()}
            {item.reason && `(${item.reason})`}
          </Typography>
        </Stack>

        <Typography variant="body1" fontWeight={500} color="text.secondary">
          {prefix}
          {formatAllowanceChargeAmount(calculatedAmount, isDocumentLevel)} {percentageText}
        </Typography>
      </Stack>

      {!readOnly && (
        <Stack direction="row" spacing={1}>
          <IconButton size="small" onClick={() => onEdit(index, type)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(index, type)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
};

export default AllowanceChargeFieldRow;
